import {
  ICustomReportColumn,
  ICustomReportDefinedColumn,
  ReportingReference,
} from '@principle-theorem/principle-core/interfaces';
import {
  IColumnSection,
  IReportBuilderDataSource,
  IReportingProperty,
} from '@principle-theorem/reporting/interfaces';
import { compact, groupBy, toPairs } from 'lodash';
import { IDataPoint } from '../measure-data-interfaces';
import { resolveReportingProperty } from '../models/fact-table-resolvers';
import { IResolvedColumnProperty } from '../models/resolved-columns';
import { ReportBuilderHelpers } from '../report-builder-helpers';

export class ReportBuilderDataSource {
  static allColumns(
    dataSource?: IReportBuilderDataSource
  ): ICustomReportDefinedColumn[] {
    if (!dataSource) {
      return [];
    }
    return dataSource?.availableColumns
      .map((section) => section.columns)
      .flat();
  }

  static findColumn(
    dataSource: IReportBuilderDataSource | undefined,
    columnId: string
  ): ICustomReportDefinedColumn | undefined {
    return this.allColumns(dataSource).find((column) => column.id === columnId);
  }

  static buildColumnSections(
    dataSource?: IReportBuilderDataSource
  ): IColumnSection[] {
    return dataSource?.availableColumns ?? [];
  }

  static groupBySection<T>(
    all: T[],
    getId: (item: T) => string,
    noSection: string = 'general'
  ): { section: string; items: T[] }[] {
    return toPairs(
      groupBy(all, (item) =>
        ReportBuilderHelpers.getSectionFromPrefix(getId(item), noSection)
      )
    ).map(([section, items]) => ({ section, items }));
  }

  static getDefaultColumns(
    dataSource?: IReportBuilderDataSource
  ): ICustomReportColumn[] {
    if (!dataSource) {
      return [];
    }
    const defaultColumns = dataSource.defaultColumns.map((column) =>
      this.findColumn(dataSource, column)
    );
    return compact(defaultColumns);
  }

  static getDataPoint(
    column: IResolvedColumnProperty,
    fact: Record<string, unknown>
  ): IDataPoint | undefined {
    return {
      label: this.getPropertyLabel(column.property),
      formatter: column.property.metadata.formatter,
      formatterValue: column.property.metadata.formatterValue,
      isPii: column.property.metadata.isPii,
      piiType: column.property.metadata.piiType,
      value: column.property.measure.dataAccessor(fact),
    };
  }

  static getPropertyLabel(property: IReportingProperty): string {
    return property.metadata.label;
  }

  static resolvePropertyLabel(
    dataSource?: IReportBuilderDataSource,
    propertyId?: ReportingReference
  ): string | undefined {
    if (!dataSource || !propertyId) {
      return;
    }
    const property = resolveReportingProperty(dataSource.factTable, propertyId);
    if (!property) {
      return;
    }
    return ReportBuilderDataSource.getPropertyLabel(property);
  }
}
